import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Box, Button, Group, NumberInput, Select, Stack, Tabs, Text } from '@mantine/core';
import { DataTable, type Column } from '../components/DataTable';
import { ExportButton } from '../components/ExportButton';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { GuestPostEditor } from './GuestPostEditor';
import classes from './EntityPage.module.css';

/**
 * «Контент для лінків» (мокап `cgen`) — екран, який віддавав `PlaceholderPage`.
 *
 * Причина, чому його не було, глибша за «не намалювали»: жодна процедура не дивилась на
 * лінкбілдинг **через усю сітку** — усе, що існувало, було в межах одного сайту. А питання,
 * заради якого цей екран відкривають, саме наскрізне: скільки з бюджету вже написано і
 * скільки розміщень доведеться дописувати.
 *
 * Шість плиток мокапа — не декор: кожна відповідає на своє питання й веде на вкладку, де з
 * цим працюють. Найважливіші дві — «сайти без плану» (бюджет проставлений, а розподіл
 * промптів порожній, тож генерація або відмовиться, або напише все одним промптом) і
 * «потребує допису» (аудит ≠ «все ок»), бо обидві означають роботу, про яку інакше не
 * дізнаєшся, доки не відкриєш кожен сайт окремо.
 */

interface PlanRow {
  id: string;
  siteId: string;
  domain: string;
  budgetPosts: number;
  anchorPct: number;
  done: number;
  inProgress: number;
  remaining: number;
  promptDistribution: Record<string, number>;
  /** Розподіл іменами профілів: ключі `promptDistribution` — це id, а не назви. */
  promptNames: Record<string, number>;
}

interface PostRow {
  id: string;
  domain: string;
  donorDomain: string | null;
  topic: string;
  anchorText: string | null;
  wordsTarget: number;
  wordsActual: number;
  status: string;
  auditStatus: string | null;
  publishedUrl: string | null;
}

const AUDIT_LABELS: Record<string, string> = {
  FOUND_OK: 'все ок',
  FOUND_TOO_SHORT: 'замало слів',
  FOUND_NO_ANCHOR: 'немає анкора',
  NOT_FOUND: 'не знайдено',
};

export function LinkContentPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<string>('domains');
  const [openPostId, setOpenPostId] = useState<string | null>(null);
  const [ticked, setTicked] = useState<ReadonlySet<string>>(new Set());
  const canWrite = useCan('linkbuilding', 'write');

  /** План, відкритий на правку з вкладки «Домени/План» (§8). */
  const [editingPlanSiteId, setEditingPlanSiteId] = useState<string | null>(null);
  const [planBudget, setPlanBudget] = useState<number>(0);
  const [planAnchorPct, setPlanAnchorPct] = useState<number>(0);
  const [planProfileId, setPlanProfileId] = useState<string | null>(null);

  const summary = useQuery(trpc.links.contentSummary.queryOptions());
  /** Лише коли редактор плану відкрито: решта вкладок його не показують. */
  const profiles = useQuery({
    ...trpc.prompts.list.queryOptions({ pagination: { limit: 100, direction: 'desc' } }),
    enabled: editingPlanSiteId !== null,
  });

  const rewrite = useMutation(
    trpc.links.rewriteBatch.mutationOptions({
      onSuccess: async () => {
        setTicked(new Set());
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );
  const savePlan = useMutation(
    trpc.links.upsertPlan.mutationOptions({
      onSuccess: async () => {
        setEditingPlanSiteId(null);
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );
  const generatePost = useMutation(
    trpc.links.generatePost.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );
  const rerunAudit = useMutation(
    trpc.links.rerunAudit.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.links.pathKey() });
      },
    }),
  );
  const plans = useQuery(trpc.links.plans.queryOptions({}));
  const posts = useQuery(trpc.links.guestPosts.queryOptions({}));

  const planColumns: Column<PlanRow>[] = [
    { key: 'domain', header: 'Домен', render: (row) => <span>{row.domain}</span> },
    {
      key: 'budget',
      header: 'Бюджет',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.budgetPosts}</span>,
    },
    {
      key: 'anchor',
      header: 'Анкорів, %',
      align: 'right',
      render: (row) => (
        <span className={row.anchorPct === 0 ? classes.danger : classes.mono}>{row.anchorPct}</span>
      ),
    },
    {
      key: 'done',
      header: 'Готово',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.done}</span>,
    },
    {
      key: 'remaining',
      header: 'Лишилось',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.remaining}</span>,
    },
    {
      key: 'prompts',
      header: 'Промпти',
      /**
       * Порожній розподіл — не косметика: `validatePromptDistribution` відмовить, а до того
       * план виглядає повним. Це і є «сайт без плану» з плитки.
       */
      render: (row) =>
        Object.keys(row.promptNames).length === 0 ? (
          <Text className={classes.danger} data-testid={`plan-no-prompts-${row.domain}`}>
            розподіл не заданий
          </Text>
        ) : (
          <span className={classes.dim}>
            {Object.entries(row.promptNames)
              .map(([name, pct]) => `${name} ${String(pct)}%`)
              .join(' · ')}
          </span>
        ),
    },
  ];

  const postColumns: Column<PostRow>[] = [
    { key: 'domain', header: 'Домен', render: (row) => <span className={classes.mono}>{row.domain}</span> },
    { key: 'topic', header: 'Тема', render: (row) => <span>{row.topic}</span> },
    {
      key: 'donor',
      header: 'Донор',
      render: (row) => <span className={classes.dim}>{row.donorDomain ?? '—'}</span>,
    },
    {
      key: 'words',
      header: 'Слів',
      align: 'right',
      /** «— / 1 200» мокапа: нуль написаних слів — це не нуль, а «тексту немає». */
      render: (row) => (
        <span className={classes.mono}>
          {row.wordsActual === 0 ? '—' : row.wordsActual} / {row.wordsTarget}
        </span>
      ),
    },
    {
      key: 'anchor',
      header: 'Анкор',
      render: (row) =>
        row.anchorText === null ? (
          <Text className={classes.dim}>—</Text>
        ) : (
          <span>{row.anchorText}</span>
        ),
    },
    {
      key: 'status',
      header: 'Стан',
      render: (row) =>
        row.auditStatus === null ? (
          <Badge size="sm" variant="default" data-testid={`cg-status-${row.id}`}>
            {row.wordsActual === 0 ? 'текст відсутній' : row.status}
          </Badge>
        ) : (
          <Badge
            size="sm"
            color={row.auditStatus === 'FOUND_OK' ? 'mint' : 'rose'}
            data-testid={`cg-status-${row.id}`}
          >
            {AUDIT_LABELS[row.auditStatus] ?? row.auditStatus}
          </Badge>
        ),
    },
  ];

  const topicColumns: Column<PostRow>[] = [
    { key: 'domain', header: 'Домен', render: (row) => <span className={classes.mono}>{row.domain}</span> },
    { key: 'topic', header: 'Тема', render: (row) => <span>{row.topic}</span> },
    {
      key: 'words',
      header: 'Ціль слів',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.wordsTarget}</span>,
    },
    {
      key: 'anchor',
      header: 'Анкор',
      render: (row) =>
        row.anchorText === null ? (
          <Text className={classes.dim}>без анкора</Text>
        ) : (
          <span>{row.anchorText}</span>
        ),
    },
    {
      key: 'action',
      header: '',
      render: (row) => (
        <Button
          size="compact-xs"
          variant="subtle"
          loading={generatePost.isPending}
          data-testid={`cg-topic-generate-${row.id}`}
          onClick={(event) => {
            event.stopPropagation();
            generatePost.mutate({ id: row.id });
          }}
        >
          згенерувати
        </Button>
      ),
    },
  ];

  const metrics = summary.data;
  const needsRework = (posts.data ?? []).filter(
    (post) => post.auditStatus !== null && post.auditStatus !== 'FOUND_OK',
  );
  /** Теми без тексту — «Теми» з мокапа: гест-пости, які ще не пішли в генерацію. */
  const topics = (posts.data ?? []).filter((post) => post.status === 'topic_ai');

  /**
   * Промпти = лінкбілдер-профілі, а вкладка показує, який розподіл планів на них спирається.
   * Рахується з уже завантажених планів, а не окремим запитом: `promptNames` в кожному плані
   * вже несе те, що треба — назву профілю й відсоток.
   */
  const promptUsage = (() => {
    const byName = new Map<string, { sitesCount: number; pctSum: number; posts: number }>();
    for (const plan of plans.data ?? []) {
      for (const [name, pct] of Object.entries(plan.promptNames)) {
        const entry = byName.get(name) ?? { sitesCount: 0, pctSum: 0, posts: 0 };
        entry.sitesCount += 1;
        entry.pctSum += pct;
        entry.posts += Math.round((plan.budgetPosts * pct) / 100);
        byName.set(name, entry);
      }
    }
    return [...byName.entries()]
      .map(([name, v]) => ({
        name,
        sitesCount: v.sitesCount,
        avgPct: Math.round(v.pctSum / v.sitesCount),
        posts: v.posts,
      }))
      .sort((a, b) => b.sitesCount - a.sitesCount);
  })();

  const promptColumns: Column<(typeof promptUsage)[number]>[] = [
    { key: 'name', header: 'Промпт', render: (row) => <span>{row.name}</span> },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.sitesCount}</span>,
    },
    {
      key: 'pct',
      header: 'Середній %',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.avgPct}</span>,
    },
    {
      key: 'posts',
      header: 'Постів у плані',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.posts}</span>,
    },
  ];

  const editingPlan = (plans.data ?? []).find((row) => row.siteId === editingPlanSiteId);

  return (
    <Stack gap="md">
      {metrics === undefined ? null : (
        <Group gap="sm" wrap="wrap" data-testid="cg-metrics">
          {[
            { key: 'planned', label: 'Сайтів у плані', value: metrics.sitesPlanned, bad: false, go: 'domains' },
            {
              key: 'incomplete',
              label: 'Сайти без плану',
              value: metrics.sitesIncomplete,
              bad: metrics.sitesIncomplete > 0,
              note: 'anchor% або промпти не задані',
              go: 'domains',
            },
            {
              key: 'prompts',
              label: 'Промптів у бібліотеці',
              value: metrics.promptsInLibrary,
              bad: false,
              go: 'prompts',
            },
            { key: 'budget', label: 'Бюджет постів', value: metrics.budgetPosts, bad: false, go: 'domains' },
            { key: 'topics', label: 'Тем згенеровано', value: metrics.topicsGenerated, bad: false, go: 'topics' },
            { key: 'written', label: 'Постів написано', value: metrics.postsWritten, bad: false, go: 'posts' },
            {
              key: 'rework',
              label: 'Потребує допису',
              value: metrics.needsRework,
              bad: metrics.needsRework > 0,
              note: 'аудит ≠ «все ок»',
              go: 'audit',
            },
          ].map((tile) => (
            // Плитка веде на вкладку, де з цим числом працюють — те саме `go: goTab(k)` мокапа.
            <Box
              key={tile.key}
              className={classes.tile}
              data-testid={`cg-tile-${tile.key}`}
              role="button"
              tabIndex={0}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                setTab(tile.go);
              }}
              onKeyDown={(event) => {
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault();
                  setTab(tile.go);
                }
              }}
            >
              <Text className={classes.tileLabel}>{tile.label}</Text>
              <Text className={tile.bad ? classes.tileValueDanger : classes.tileValue}>
                {tile.value}
              </Text>
              {tile.note === undefined ? null : (
                <Text className={classes.dim}>{tile.note}</Text>
              )}
            </Box>
          ))}
        </Group>
      )}

      <Tabs
        keepMounted={false}
        value={tab}
        onChange={(value: string | null) => {
          setTab(value ?? 'domains');
        }}
        data-testid="cg-tabs"
      >
        <Tabs.List>
          <Tabs.Tab value="domains" data-testid="cg-tab-domains">
            Домени / План
          </Tabs.Tab>
          <Tabs.Tab value="prompts" data-testid="cg-tab-prompts">
            Промпти
          </Tabs.Tab>
          <Tabs.Tab value="topics" data-testid="cg-tab-topics">
            Теми
          </Tabs.Tab>
          <Tabs.Tab value="posts" data-testid="cg-tab-posts">
            Гестпости
          </Tabs.Tab>
          <Tabs.Tab value="audit" data-testid="cg-tab-audit">
            Аудит
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="domains" pt="sm">
          <DataTable
            rows={plans.data ?? []}
            columns={planColumns}
            loading={plans.isPending}
            error={plans.error}
            emptyMessage="Планів лінкбілдингу немає. Заведіть план на екрані «Лінкбілдинг»."
            onRowClick={(row) => {
              setEditingPlanSiteId(row.siteId);
              setPlanBudget(row.budgetPosts);
              setPlanAnchorPct(row.anchorPct);
              setPlanProfileId(Object.keys(row.promptDistribution)[0] ?? null);
            }}
            rowTestId={(row) => `cg-plan-${row.domain}`}
            testId="cg-plans"
          />

          {/*
           * Керування планом просто з cgen — до цього бюджет, anchor% і розподіл промптів
           * правили лише на «Лінкбілдингу», а проблему («сайт без плану») бачать саме тут.
           */}
          {editingPlanSiteId === null || editingPlan === undefined ? null : (
            <Box className={classes.panel} mt="sm" data-testid="cg-plan-editor">
              <Group justify="space-between" mb={8}>
                <Text className={classes.panelTitle}>План — {editingPlan.domain}</Text>
                <Button
                  size="compact-xs"
                  variant="subtle"
                  data-testid="cg-plan-editor-close"
                  onClick={() => {
                    setEditingPlanSiteId(null);
                  }}
                >
                  Закрити
                </Button>
              </Group>
              <Group gap="md" align="flex-end">
                <NumberInput
                  label="Бюджет постів"
                  size="xs"
                  min={0}
                  max={100_000}
                  value={planBudget}
                  onChange={(value) => {
                    setPlanBudget(typeof value === 'number' ? value : 0);
                  }}
                  data-testid="cg-plan-budget"
                  style={{ width: 140 }}
                />
                <NumberInput
                  label="Анкорних, %"
                  size="xs"
                  min={0}
                  max={100}
                  value={planAnchorPct}
                  onChange={(value) => {
                    setPlanAnchorPct(typeof value === 'number' ? value : 0);
                  }}
                  data-testid="cg-plan-anchor-pct"
                  style={{ width: 140 }}
                />
                <Select
                  label="Промпт-профіль (100%)"
                  data={(profiles.data?.items ?? []).map((profile) => ({
                    value: profile.id,
                    label: profile.name,
                  }))}
                  value={planProfileId}
                  onChange={setPlanProfileId}
                  size="xs"
                  data-testid="cg-plan-profile"
                  style={{ width: 220 }}
                />
                <Button
                  size="xs"
                  disabled={planProfileId === null}
                  loading={savePlan.isPending}
                  data-testid="cg-plan-save"
                  onClick={() => {
                    if (planProfileId !== null) {
                      savePlan.mutate({
                        siteId: editingPlanSiteId,
                        budgetPosts: planBudget,
                        anchorPct: planAnchorPct,
                        promptDistribution: { [planProfileId]: 100 },
                      });
                    }
                  }}
                >
                  Зберегти план
                </Button>
              </Group>
              {savePlan.error === null ? null : (
                <Text className={classes.danger} mt={8} data-testid="cg-plan-error">
                  {publicMessageOf(savePlan.error)}
                </Text>
              )}
            </Box>
          )}
        </Tabs.Panel>

        <Tabs.Panel value="prompts" pt="sm">
          {/*
           * Промпти лінкбілдингу — ті самі профілі, що на «Лінкбілдингу»: вкладка показує, які
           * з них і на скількох сайтах несе розподіл планів, а не заводить другий реєстр.
           */}
          <DataTable
            rows={promptUsage}
            columns={promptColumns}
            loading={plans.isPending}
            error={plans.error}
            emptyMessage="Жоден план ще не має розподілу промптів."
            rowTestId={(row) => `cg-prompt-${row.name}`}
            testId="cg-prompts"
          />
        </Tabs.Panel>

        <Tabs.Panel value="topics" pt="sm">
          <DataTable
            rows={topics}
            columns={topicColumns}
            loading={posts.isPending}
            error={posts.error}
            emptyMessage="Тем без тексту немає. Згенеруйте їх на екрані «Лінкбілдинг»."
            onRowClick={(row) => {
              setOpenPostId(row.id);
            }}
            rowTestId={(row) => `cg-topic-${row.id}`}
            testId="cg-topics"
          />
        </Tabs.Panel>

        <Tabs.Panel value="posts" pt="sm">
          <DataTable
            rows={posts.data ?? []}
            columns={postColumns}
            loading={posts.isPending}
            error={posts.error}
            emptyMessage="Гест-постів немає. Згенеруйте теми на екрані «Лінкбілдинг»."
            onRowClick={(row) => {
              setOpenPostId(row.id);
            }}
            rowTestId={(row) => `cg-post-${row.id}`}
            testId="cg-posts"
            footerActions={
              <ExportButton dataset="guestPosts" zone="linkbuilding" label="Експорт у роботу" />
            }
          />
        </Tabs.Panel>

        <Tabs.Panel value="audit" pt="sm">
          {/*
           * Вкладка аудиту — те саме, що «Гестпости», але звужене до розміщень, які треба
           * дописати. Окремою вкладкою, бо це черга роботи, а не перегляд: у переліку всіх
           * постів ці рядки губляться серед сотень готових.
           */}
          {/*
           * Партія аудиту — тут, а не лише на «Лінкбілдингу»: черга дописування живе саме на
           * цій вкладці, і оператору, який дивиться на 392 «потребує допису», перевірка
           * потрібніша тут, ніж на екрані одного сайту.
           */}
          <Group justify="flex-end" mb={8}>
            <Button
              size="compact-xs"
              variant="default"
              loading={rerunAudit.isPending}
              data-testid="cg-audit-rerun"
              onClick={() => {
                rerunAudit.mutate({});
              }}
            >
              Перепрогнати аудит
            </Button>
          </Group>
          {rerunAudit.data === undefined ? null : (
            <Text className={classes.dim} mb={8} data-testid="cg-audit-rerun-result">
              перевірено {rerunAudit.data.checked} · потребують допису {rerunAudit.data.needRegeneration.length}
            </Text>
          )}
          {rerunAudit.error === null ? null : (
            <Text className={classes.danger} mb={8} data-testid="cg-audit-rerun-error" role="alert">
              {publicMessageOf(rerunAudit.error)}
            </Text>
          )}
          <DataTable
            rows={needsRework}
            columns={postColumns}
            loading={posts.isPending}
            error={posts.error}
            emptyMessage="Усі перевірені розміщення пройшли аудит."
            onRowClick={(row) => {
              setOpenPostId(row.id);
            }}
            rowTestId={(row) => `cg-audit-${row.id}`}
            testId="cg-audit"
            selection={{ idOf: (row) => row.id, selected: ticked, onChange: setTicked }}
            footerActions={
              canWrite ? (
                /*
                 * Масовий ре-райт стоїть саме тут, а не в переліку всіх постів: черга роботи
                 * — це те, що не пройшло аудит, і переписувати те, що пройшло, немає навіщо.
                 */
                <Button
                  size="compact-xs"
                  loading={rewrite.isPending}
                  disabled={ticked.size === 0}
                  data-testid="cg-rewrite"
                  onClick={() => {
                    rewrite.mutate({ guestPostIds: [...ticked] });
                  }}
                >
                  Переписати вибрані · {ticked.size}
                </Button>
              ) : null
            }
          />

          {rewrite.data === undefined ? null : (
            <Text className={classes.dim} mt={6} data-testid="cg-rewrite-result">
              поставлено в чергу: {rewrite.data.queued}
              {rewrite.data.duplicates.length === 0
                ? ''
                : ` · вже стояли: ${String(rewrite.data.duplicates.length)}`}
            </Text>
          )}
          {rewrite.error === null ? null : (
            <Text className={classes.danger} mt={6} data-testid="cg-rewrite-error" role="alert">
              {publicMessageOf(rewrite.error)}
            </Text>
          )}
        </Tabs.Panel>
      </Tabs>

      {openPostId === null ? null : (
        <GuestPostEditor
          key={openPostId}
          postId={openPostId}
          onClose={() => {
            setOpenPostId(null);
          }}
        />
      )}
    </Stack>
  );
}
